import { TEmployeeRenewVisaDocumentStepGroup } from "@/api/entities/employee-renew-visa-document-step-group.entity";
import { createOrUpdateRenewVisaApplicationResultInformationGroupListApi } from "@/api/services/main/renew-visa";
import { TGetRenewVisaApplicationResultInformationGroupOutputDto } from "@/api/services/main/renew-visa/dtos/get-renew-visa-application-result-information-group.dto";
import { Avatar } from "@/components/elements/Avatar";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { Table } from "@/components/elements/Table";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { RenewVisaStepCard } from "@/features/renew-visa/components/RenewVisaStepCard";
import { useCheckRenewVisaHistoryPage } from "@/features/renew-visa/hook/useCheckRenewVisaHistoryPage";
import { renewVisaApplicationResultInformationSchema } from "@/features/renew-visa/schema/renewVisaApplicationResultInformationSchema";
import { TRenewVisaApplicationResultInformationForm } from "@/features/renew-visa/types/renewVisaApplicationResultInformationForm";
import { useTable } from "@/hooks/useTable";
import { divider, text } from "@/theme/colors";
import { RENEW_VISA_APPLICATION_RESULT_LABEL, RENEW_VISA_DOCUMENT_STEP_STATUS } from "@/types/enum";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { getFullName, truncateWithEllipsis } from "@/utils/string";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

export type TAfterSubmitApplicationResultStepCardProps = {
  stepGroupData: TEmployeeRenewVisaDocumentStepGroup;
  applicationResultInformationGroupListData?: TGetRenewVisaApplicationResultInformationGroupOutputDto[];
  readonly: boolean;
  isExistBasicInfoData: boolean;
};

export const AfterSubmitApplicationResultStepCard: FC<TAfterSubmitApplicationResultStepCardProps> = ({
  stepGroupData,
  isExistBasicInfoData,
  applicationResultInformationGroupListData,
  readonly,
}) => {
  const { stepOrder, stepName } = stepGroupData.masterDocumentStep;
  const { id: documentStepGroupId } = stepGroupData;

  const { employeeRenewVisaDocumentGroupId } = useParams();
  const queryClient = useQueryClient();

  const methods = useForm<TRenewVisaApplicationResultInformationForm>({
    resolver: yupResolver(renewVisaApplicationResultInformationSchema),
    defaultValues: {
      applicationResultInformation: applicationResultInformationGroupListData?.map((item) => ({
        renewVisaDocumentStepId: toNumber(item.renewVisaDocumentStep.id),
        renewVisaDocumentId: toNumber(item.renewVisaDocumentId),
        applicationResult: item.renewVisaDocumentStep.renewVisaApplicationResultInformation?.applicationResult,
        immigrationMailTrackingNumber: item.renewVisaDocumentStep.renewVisaApplicationResultInformation?.immigrationMailTrackingNumber,
        replyEnvelopeTrackingNumber: item.renewVisaDocumentStep.renewVisaApplicationResultInformation?.replyEnvelopeTrackingNumber,
      })),
    },
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control } = methods;

  const onSubmitRenewVisaApplicaitonResultInformation = async (data: TRenewVisaApplicationResultInformationForm) => {
    if (!employeeRenewVisaDocumentGroupId || !documentStepGroupId) return;
    try {
      const rs = await createOrUpdateRenewVisaApplicationResultInformationGroupListApi(employeeRenewVisaDocumentGroupId, documentStepGroupId, {
        applicationResultInformation: data.applicationResultInformation.map((item) => ({
          renewVisaDocumentStepId: item.renewVisaDocumentStepId,
          renewVisaDocumentId: item.renewVisaDocumentId,
          applicationResult: item.applicationResult,
          immigrationMailTrackingNumber: item.immigrationMailTrackingNumber,
          replyEnvelopeTrackingNumber: item.replyEnvelopeTrackingNumber,
        })),
      });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_GROUP_DETAIL] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_RESIDENCE_CARD_INFORMATION_GROUP] });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
    } catch (error) {
      showError(error);
    }
  };

  const { isHistoryPage } = useCheckRenewVisaHistoryPage();

  const columns = useMemo<MRT_ColumnDef<TGetRenewVisaApplicationResultInformationGroupOutputDto>[]>(
    () => [
      {
        header: "外国人名",
        Cell: ({ row }) => {
          const fullName = getFullName({
            firstName: row.original.employee.firstName,
            lastName: row.original.employee.lastName,
            middleName: row.original.employee.middleName,
          });
          return (
            <Box display="flex" alignItems="center" gap={1.75}>
              <Avatar filePath={row.original.employee.thumbnailForListAvatarFilePath} alt="avatar" width={30} height={40} />
              <Typography variant="body14" padding="0px !important">
                {fullName ? truncateWithEllipsis(fullName, 26) : EMPTY_STR.TEXT}
              </Typography>
            </Box>
          );
        },
      },
      {
        header: "申請結果",
        Header: () => (
          <Typography padding="0px !important" component="p" variant="sub16Bold">
            申請結果
            <Typography padding="0px !important" component="span" color={text.error}>
              *
            </Typography>
          </Typography>
        ),
        Cell: ({ row }) => {
          if (isHistoryPage)
            return (
              <Typography variant="body14" padding="0px !important">
                {row.original.renewVisaDocumentStep.renewVisaApplicationResultInformation?.applicationResult
                  ? RENEW_VISA_APPLICATION_RESULT_LABEL[row.original.renewVisaDocumentStep.renewVisaApplicationResultInformation?.applicationResult]
                  : EMPTY_STR.TEXT}
              </Typography>
            );
          return (
            <FormField control={control} name={`applicationResultInformation.${row.index}.applicationResult`}>
              <Select enumName="ERenewVisaApplicationResult" disabled={readonly} />
            </FormField>
          );
        },
      },
      {
        header: "入管送付郵便の追跡番号",
        Cell: ({ row }) => {
          if (isHistoryPage)
            return (
              <Typography variant="body14" padding="0px !important">
                {row.original.renewVisaDocumentStep.renewVisaApplicationResultInformation?.immigrationMailTrackingNumber}
              </Typography>
            );
          return (
            <FormField control={control} name={`applicationResultInformation.${row.index}.immigrationMailTrackingNumber`}>
              <Input disabled={readonly} />
            </FormField>
          );
        },
      },
      {
        header: "返信用封筒の追跡番号",
        Cell: ({ row }) => {
          if (isHistoryPage)
            return (
              <Typography variant="body14" padding="0px !important">
                {row.original.renewVisaDocumentStep.renewVisaApplicationResultInformation?.replyEnvelopeTrackingNumber}
              </Typography>
            );
          return (
            <FormField control={control} name={`applicationResultInformation.${row.index}.replyEnvelopeTrackingNumber`}>
              <Input disabled={readonly} />
            </FormField>
          );
        },
      },
    ],
    [readonly],
  );

  const { table } = useTable({
    columns,
    rows: applicationResultInformationGroupListData ?? [],
    muiTableHeadCellProps: { sx: { backgroundColor: divider.low } },
  });

  return (
    <Form methods={methods} onSubmit={onSubmitRenewVisaApplicaitonResultInformation} schema={renewVisaApplicationResultInformationSchema}>
      <RenewVisaStepCard
        isCompleted={stepGroupData.status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE}
        title={`ステップ${isExistBasicInfoData ? stepOrder : stepOrder - 1}`}
        subTitle={stepName}
        buttons={
          <Button variant="primary" size="sm" type="submit" disabled={readonly}>
            保存
          </Button>
        }
      >
        <Typography component="p" variant="body14" mb="16px" color={text.secondary}>
          申請結果を選択して保存してください。取り下げ、または不許可になった場合は、以下を変更して保存することで本申請を終了します。 <br />
          申請許可を選択した場合はステップ{isExistBasicInfoData ? 6 : 5}に進みます。
        </Typography>
        <Table table={table} maxHeight="45vw" />
      </RenewVisaStepCard>
    </Form>
  );
};
